import { useState, useEffect, useRef, useMemo } from 'react';
import { useContacts } from '../contexts/ContactsContext';
import { useInvoices } from '../contexts/InvoicesContext';

const NAVY = '#0C2D48';
const YELLOW = '#E4E69B';

function highlight(text, query) {
  if (!query || !text) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: YELLOW, color: NAVY, borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

function searchAll(query, notes = [], contacts = [], invoices = [], tasks = []) {
  if (!query || query.length < 2) return [];
  const q = query.toLowerCase();
  const results = [];

  // Tasks — from the real workspace-scoped task list (passed in), not localStorage.
  for (const t of tasks) {
    if (
      t.title?.toLowerCase().includes(q) ||
      t.details?.toLowerCase().includes(q) ||
      t.notes?.toLowerCase().includes(q)
    ) {
      results.push({
        id: `task_${t.id}`,
        type: 'task',
        icon: t.taskType === 'Meeting' ? '📅' : '✅',
        label: t.title,
        sub: t.date ? new Date(t.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'No date',
        data: t,
        section: 'Schedule',
      });
    }
  }

  // Notes (from in-memory prop)
  for (const n of notes) {
    const noteText = n.text || n.body || '';
    if (n.title?.toLowerCase().includes(q) || noteText.toLowerCase().includes(q)) {
      results.push({
        id: `note_${n.id}`,
        type: 'note',
        icon: '📝',
        label: n.title || 'Untitled note',
        sub: noteText.slice(0, 60),
        data: n,
        section: 'Notes',
      });
    }
  }

  // Contacts (from in-memory context)
  for (const c of contacts) {
    if (
      c.name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q) ||
      c.company?.toLowerCase().includes(q)
    ) {
      results.push({
        id: `contact_${c.id}`,
        type: 'contact',
        icon: '👤',
        label: c.name,
        sub: [c.company, c.email].filter(Boolean).join(' · '),
        data: c,
        section: 'Contacts',
      });
    }
  }

  // Invoices
  for (const inv of invoices) {
    if (
      inv.contactName?.toLowerCase().includes(q) ||
      inv.number?.toLowerCase().includes(q) ||
      inv.notes?.toLowerCase().includes(q)
    ) {
      results.push({
        id: `inv_${inv.id}`,
        type: 'invoice',
        icon: '🧾',
        label: inv.number || `Invoice ${inv.id}`,
        sub: `${inv.contactName || ''} · ${inv.status || ''}`,
        data: inv,
        section: 'Invoices',
      });
    }
  }

  return results.slice(0, 12);
}

const SECTION_ICON = {
  Schedule: '📅',
  Notes: '📝',
  Contacts: '👤',
  Invoices: '🧾',
};

export default function SearchOverlay({ onClose, onNavigate, onViewTask, onViewNote, notes = [], tasks = [] }) {
  const { contacts } = useContacts();
  const { invoices } = useInvoices();
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);

  const results = useMemo(() => searchAll(query, notes, contacts, invoices, tasks), [query, notes, contacts, invoices, tasks]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  function handleSelect(result) {
    onClose();
    if (result.type === 'task' && onViewTask) {
      onViewTask(result.data);
    } else if (result.type === 'note' && onViewNote) {
      onViewNote(result.data);
    } else if (onNavigate) {
      onNavigate(result.section);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[selectedIdx]) { handleSelect(results[selectedIdx]); }
  }

  // Group results by section
  const grouped = useMemo(() => {
    const map = {};
    results.forEach(r => {
      if (!map[r.section]) map[r.section] = [];
      map[r.section].push(r);
    });
    return Object.entries(map);
  }, [results]);

  let flatIndex = 0;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          zIndex: 9000, backdropFilter: 'blur(2px)',
        }}
      />

      {/* Panel */}
      <div
        style={{
          position: 'fixed', top: '10vh', left: '50%', transform: 'translateX(-50%)',
          width: '100%', maxWidth: 580,
          background: '#fff', borderRadius: 14,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          zIndex: 9001, overflow: 'hidden',
          fontFamily: "'Raleway', sans-serif",
        }}
      >
        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0.9rem 1rem', borderBottom: '1px solid #f0f0f0', gap: '0.6rem' }}>
          <span style={{ fontSize: '1.15rem', color: '#aaa' }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search tasks, notes, contacts, invoices…"
            style={{
              flex: 1, border: 'none', outline: 'none',
              fontSize: '1rem', fontFamily: "'Raleway', sans-serif",
              color: '#333',
            }}
          />
          <kbd style={{ fontSize: '0.72rem', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 4, padding: '2px 6px', color: '#888', fontFamily: 'monospace' }}>
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {query.length < 2 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#bbb', fontSize: '0.88rem' }}>
              Type to search across tasks, notes, contacts and invoices
            </div>
          ) : results.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#bbb', fontSize: '0.88rem' }}>
              No results for "{query}"
            </div>
          ) : (
            grouped.map(([section, items]) => (
              <div key={section}>
                <div style={{ padding: '0.5rem 1rem 0.2rem', fontSize: '0.68rem', fontWeight: 700, color: '#bbb', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                  {SECTION_ICON[section]} {section}
                </div>
                {items.map(result => {
                  const isSelected = flatIndex === selectedIdx;
                  const currentIndex = flatIndex++;
                  return (
                    <div
                      key={result.id}
                      onClick={() => handleSelect(result)}
                      onMouseEnter={() => setSelectedIdx(currentIndex)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        padding: '0.6rem 1rem',
                        background: isSelected ? '#f0f5f9' : '#fff',
                        cursor: 'pointer',
                        borderLeft: isSelected ? `3px solid ${NAVY}` : '3px solid transparent',
                        transition: 'background 0.1s',
                      }}
                    >
                      <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{result.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {highlight(result.label, query)}
                        </div>
                        {result.sub && (
                          <div style={{ fontSize: '0.75rem', color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {highlight(result.sub, query)}
                          </div>
                        )}
                      </div>
                      {isSelected && (
                        <kbd style={{ fontSize: '0.68rem', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 4, padding: '2px 5px', color: '#888', fontFamily: 'monospace', flexShrink: 0 }}>
                          ↵
                        </kbd>
                      )}
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div style={{ padding: '0.5rem 1rem', borderTop: '1px solid #f5f5f5', display: 'flex', gap: '1rem', fontSize: '0.72rem', color: '#ccc' }}>
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>ESC close</span>
        </div>
      </div>
    </>
  );
}
